import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ReportsApi } from "apis/ReportsApi";
import { ReportExportBody } from "models/common/storeGroupMessag";
import { ReportsFilterQuery } from "models/filters/reports/ReportsFilterQuery";
import { StockReportFunction } from "models/functionModels/reportFunctions/StockReportFunction";
import { TillReportFunction } from "models/functionModels/reportFunctions/TillReportFunction";
import { Report } from "models/dbModels/reports/Report";
import { SalesReportFunction } from "models/functionModels/reportFunctions/SalesReportFunction";

export const getReports = createAsyncThunk("reportsSlice/getReports", async (params: ReportsFilterQuery) => {
    const response = await ReportsApi.Search(params);
    return response.data;
});

export const getReport = createAsyncThunk("reportsSlice/getReport", async (params: ReportsFilterQuery) => {
    const response = await ReportsApi.Search(params);
    return response.data.data[0];
});

export const generateStockReport = createAsyncThunk("reportsSlice/generateStockReport", async (params: StockReportFunction) => {
    const response = await ReportsApi.StockReport(params).catch(error => {
        console.log("Error generating stock report", error);
        return error;
    });
  
    return response.data.stores[0];
});

export const generateSalesReport = createAsyncThunk("reportsSlice/generateSalesReport", async (params: SalesReportFunction) => {
    const response = await ReportsApi.SalesReportNormal(params);
    return response.data.stores[0];
});


export const generateGroupTillReport = createAsyncThunk("reportsSlice/generateGroupTillReport", async (params: TillReportFunction) => {
    const response = await ReportsApi.TillReport(params);
    return response.data;
});

export const exportStockReport = createAsyncThunk("reportsSlice/exportStockReport", async (params: ReportExportBody<StockReportFunction>) => {
    const response = await ReportsApi.ExportStockReport(params);
    return response.data;
});

export const exportTillReport = createAsyncThunk("reportsSlice/tillReportExport", async (params: ReportExportBody<TillReportFunction>) => {
    const response = await ReportsApi.TillReportExport(params);
    return response.data;
});

type InitialStateType = {
    id: number;
    name: string;
    description: string;
    reportType: string;
    filter: string;
    reports: Report[];

    startDate: string;
    endDate: string;

    table: any[];
    loading: boolean;
    keyword: string;
    totalRows: number;
    totalPages: number;
    pageIndex: number;
    selectedRows: any[];
    error: string;
};

const initialState: InitialStateType = {
      id: 0,
      name: "",
      description: "",
      reportType: "",
      filter: "",
      reports: [],
      startDate: "",
      endDate: "",      
      table:  [],
      loading: false,
      keyword: "",
      totalRows: 0,
      totalPages: 0,
      pageIndex: 0,
      selectedRows: [],
      error: "",      
};


const reportsSlice = createSlice({
    name: "reportsSlice",
    initialState: {
        ...initialState,
    },
    reducers: {
        cleanReports: (state) => {
            return {
                ...state,
                ...initialState
            };
        },
        updateReportField: (state, action) => {
            return {
                ...state,
                [action.payload.key]: action.payload.value
            };
        }
    },
    extraReducers: (builder) => {
        builder.addCase(getReports.fulfilled, (state, action) => {
            return {
                ...state,
                reports: action.payload.data,
                totalRows: action.payload.totalRows,
            };
        });
        builder.addCase(getReport.fulfilled, (state, action) => {
            return {
                ...state,
                ...action.payload
            }
        });
        builder.addCase(generateStockReport.pending, (state, action) => {
            return {
                ...state,
                loading: true,
                table: [],
            };
        });

        builder.addCase(generateStockReport.fulfilled, (state, action) => {
            state.loading = false;
            state.error = action.payload.errors.length > 0 ? action.payload.errors[0] : "";
            state.table = action.payload.response?.data?.rows ?? [];
            state.totalRows = action.payload.response?.totalRows ?? 0;
            state.totalPages = Math.ceil(Math.ceil(state.totalRows) / 100);
        });
        builder.addCase(generateStockReport.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || "";
        } );



        builder.addCase(generateSalesReport.pending, (state, action) => {
            return {
                ...state,
                loading: true,
                table: [],
            };
        });

        builder.addCase(generateSalesReport.fulfilled, (state, action) => {
            state.loading = false;
            state.error = action.payload.errors.length > 0 ? action.payload.errors[0] : "";
            state.table = action.payload.response?.data?.rows ?? [];
            state.totalRows = action.payload.response?.totalRows ?? 0;
            state.totalPages = Math.ceil(Math.ceil(state.totalRows) / 100);
        });
        builder.addCase(generateSalesReport.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || "";
        } );


        builder.addCase(generateGroupTillReport.pending, (state, action) => {
            return {
                ...state,
                loading: true,
                table: [],
            };
        });

        builder.addCase(generateGroupTillReport.fulfilled, (state, action) => {
            state.loading = false;
            state.error = action.payload.errors.length > 0 ? action.payload.errors[0] : "";
            state.table = action.payload.stores ?? [];
            state.totalPages = Math.ceil(Math.ceil(state.totalRows) / 100);
        });
        builder.addCase(generateGroupTillReport.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || "";
        } );


        builder.addCase(exportStockReport.pending, (state, action) => {
            return {
                ...state,
                loading: true,
            };
        });

        builder.addCase(exportStockReport.fulfilled, (state, action) => {
            state.loading = false;
        });

        builder.addCase(exportStockReport.rejected, (state, action) => {
            state.loading = false;
        });

        builder.addCase(exportTillReport.pending, (state, action) => {
            return {
                ...state,
                loading: true,
            };
        });

        builder.addCase(exportTillReport.fulfilled, (state, action) => {
            state.loading = false;
        });

        builder.addCase(exportTillReport.rejected, (state, action) => {
            state.loading = false;
        });
    }
});

export const { cleanReports, updateReportField } = reportsSlice.actions

export default reportsSlice.reducer;